/**
 * Onboarding de instalação.
 *
 * Regra aprovada: ensinar, nunca bloquear. O núcleo do app continua utilizável
 * no navegador; instalar é o modo recomendado e, no iOS, condição para push.
 * Depois de instalada — ou dispensada — não aparece de novo.
 */
import { useEffect, useState } from 'react';

import { detectPlatform, installCapability, isStandalone } from './detect';

const DISPENSADO = 'nv.install.dismissed';

export function InstallHint() {
  const [prompt, setPrompt] = useState<Event | null>(null);
  const [dispensado, setDispensado] = useState(() => {
    try {
      return localStorage.getItem(DISPENSADO) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const capturar = (event: Event) => {
      event.preventDefault();
      setPrompt(event);
    };
    window.addEventListener('beforeinstallprompt', capturar);
    return () => window.removeEventListener('beforeinstallprompt', capturar);
  }, []);

  if (dispensado) return null;

  const capability = installCapability({
    platform: detectPlatform(navigator.userAgent),
    standalone: isStandalone(
      (q) => window.matchMedia(q),
      (navigator as { standalone?: boolean }).standalone,
    ),
    hasDeferredPrompt: prompt !== null,
  });

  if (capability.kind === 'none') return null;

  const dispensar = () => {
    setDispensado(true);
    try {
      localStorage.setItem(DISPENSADO, '1');
    } catch {
      // Sem persistência, reaparece na próxima sessão. Aceitável.
    }
  };

  return (
    <aside className="install-hint" aria-label="Instalar o Viajandos">
      {capability.kind === 'instructions' ? (
        <p className="install-hint__text">
          Para a experiência completa e para receber avisos, toque em{' '}
          <strong>Compartilhar</strong> e depois em{' '}
          <strong>Adicionar à Tela de Início</strong>.
        </p>
      ) : (
        <p className="install-hint__text">
          Instale o Viajandos na tela de início para abrir mais rápido e
          receber avisos.
        </p>
      )}

      <div className="install-hint__actions">
        {capability.kind === 'prompt' && (
          <button
            type="button"
            className="install-hint__install"
            onClick={() => {
              void (prompt as { prompt?: () => Promise<void> } | null)?.prompt?.();
              dispensar();
            }}
          >
            Instalar
          </button>
        )}
        <button type="button" className="install-hint__dismiss" onClick={dispensar}>
          Agora não
        </button>
      </div>
    </aside>
  );
}
